import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, Link } from 'wouter';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { LogIn, Eye, EyeOff, AlertCircle, Mail } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { setSessionIndicator } from '@/hooks/useAuth';
import logoImage from "@assets/openbox_1762897846924.png";

export default function LoginPage() {
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [showLoginHelpDialog, setShowLoginHelpDialog] = useState(false);
  const [loginErrorReason, setLoginErrorReason] = useState<string>('');
  
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('session_expired') === 'true') {
      setSessionExpired(true);
    }
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      console.log('[Login] Attempting login for:', email);
      const response = await fetch('/api/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Important for session cookies
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();
      console.log('[Login] Response status:', response.status, 'Data:', data);

      if (response.ok) {
        // Set flag indicating active session (allows useAuth to skip unnecessary API calls)
        // Use setSessionIndicator to properly reset auth error state and notify all subscribers
        setSessionIndicator();

        // Force a fresh fetch of the authenticated user so the nav bar picks up
        // the new account's roles immediately (e.g. the Admin link). Without this,
        // React Query's staleTime can serve cached/empty user data, leaving the
        // Admin link hidden until the user navigates somewhere that refetches it.
        await queryClient.refetchQueries({ queryKey: ["/api/auth/user"], type: "all" });

        // If the account is locked/inactive, show a clear dialog instead of
        // silently redirecting. The user is still logged in (so they can
        // browse and manage their cart) but checkout will be disabled.
        if (data.accountInactive) {
          setLoginErrorReason('ACCOUNT_INACTIVE');
          setShowLoginHelpDialog(true);
          return;
        }

        toast({
          title: 'Welcome back!',
          description: 'You have successfully logged in.',
        });
        // Small delay to ensure session is saved
        setTimeout(() => {
          // Check for return URL from session expiration, auth redirect, or checkout
          const returnUrl = sessionStorage.getItem('returnUrl') || sessionStorage.getItem('auth_redirect') || sessionStorage.getItem('checkout_redirect');
          if (returnUrl) {
            sessionStorage.removeItem('returnUrl');
            sessionStorage.removeItem('auth_redirect');
            sessionStorage.removeItem('checkout_redirect');
            setLocation(returnUrl);
          } else {
            setLocation('/');
          }
        }, 100);
      } else {
        if (data.reason !== 'EMAIL_NOT_FOUND') {
          console.error(`[Login] Failed for "${email}":`, data.error);
        }
        setLoginErrorReason(data.reason || '');
        setShowLoginHelpDialog(true);
      }
    } catch (error) {
      console.error('[Login] Exception:', error);
      toast({
        title: 'Error',
        description: 'An error occurred during login. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-3 sm:px-4 py-6 sm:py-12">
      <Card className="w-full max-w-md" data-testid="card-login">
        <CardHeader className="text-center p-4 sm:p-6">
          <div className="flex justify-center mb-3 sm:mb-4">
            <img 
              src={logoImage} 
              alt="OpenBox by ShopJimmy" 
              className="h-12 sm:h-16 w-auto object-contain"
            />
          </div>
          <CardTitle className="text-xl sm:text-2xl">Welcome Back</CardTitle>
          <CardDescription className="text-xs sm:text-sm">
            Sign in to access OpenBox by ShopJimmy
          </CardDescription>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0">
          {sessionExpired && (
            <Alert variant="destructive" className="mb-4" data-testid="alert-session-expired">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Your session has expired. Please log in again to continue.
              </AlertDescription>
            </Alert>
          )}
          <form onSubmit={handleLogin} className="space-y-3 sm:space-y-4">
            <div className="space-y-1.5 sm:space-y-2">
              <Label htmlFor="email" className="text-sm">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading}
                data-testid="input-email"
                className="h-10 sm:h-11 text-sm sm:text-base"
              />
            </div>
            <div className="space-y-1.5 sm:space-y-2">
              <div className="flex justify-between items-center">
                <Label htmlFor="password" className="text-sm">Password</Label>
                <Link 
                  href="/forgot-password" 
                  className="text-xs text-primary hover:underline"
                  data-testid="link-forgot-password"
                >
                  Forgot Password?
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={isLoading}
                  data-testid="input-password"
                  className="h-10 sm:h-11 text-sm sm:text-base pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="button-toggle-password"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>
            <Button 
              type="submit" 
              className="w-full h-10 sm:h-11 text-sm sm:text-base" 
              disabled={isLoading}
              data-testid="button-login"
            >
              <LogIn className="h-4 w-4 mr-1.5 sm:mr-2" />
              {isLoading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
          
          <div className="text-center text-[11px] sm:text-sm text-muted-foreground mt-3 sm:mt-4">
            <p>
              By signing in, you agree to our{' '}
              <a href="/terms" className="underline hover:text-foreground" data-testid="link-login-terms">
                Terms of Service
              </a>{' '}
              and Privacy Policy
            </p>
          </div>
          
          <div className="text-center mt-4 sm:mt-6">
            <p className="text-xs sm:text-sm text-muted-foreground">
              Don't have an account?{' '}
              <a href="/signup" className="text-primary hover:underline font-medium">
                Apply Now
              </a>
            </p>
          </div>
        </CardContent>
      </Card>

      <Dialog open={showLoginHelpDialog} onOpenChange={setShowLoginHelpDialog}>
        <DialogContent className="sm:max-w-lg" data-testid="dialog-login-help">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <AlertCircle className="h-6 w-6 text-amber-500" />
              <span className="text-amber-700 dark:text-amber-400">
                {loginErrorReason === 'EMAIL_NOT_FOUND'
                  ? 'No Account Found'
                  : loginErrorReason === 'ACCOUNT_INACTIVE'
                    ? 'Account Locked'
                    : 'Login Unsuccessful'}
              </span>
            </DialogTitle>
            <DialogDescription asChild>
              <div className="text-left space-y-4 pt-3">
                {loginErrorReason === 'ACCOUNT_INACTIVE' ? (
                  <>
                    <div className="bg-amber-50 dark:bg-amber-950/40 rounded-lg p-4 border-2 border-amber-300 dark:border-amber-700">
                      <p className="text-amber-800 dark:text-amber-200 font-medium text-base">
                        Your account has been locked and you are unable to place orders on our site.
                      </p>
                      <p className="text-amber-700 dark:text-amber-300 text-sm mt-2">
                        You can still browse our inventory and add items to your cart, but you won't be able to check out until your account is reactivated.
                      </p>
                    </div>

                    <div className="bg-muted/50 rounded-lg p-3 border">
                      <p className="text-sm text-muted-foreground">
                        If you believe this is an error, please contact us so we can help reactivate your account.
                      </p>
                    </div>
                  </>
                ) : loginErrorReason === 'EMAIL_NOT_FOUND' ? (
                  <>
                    <div className="bg-blue-50 dark:bg-blue-950/30 rounded-lg p-4 border-2 border-blue-300 dark:border-blue-700">
                      <p className="text-blue-800 dark:text-blue-200 font-medium text-base">
                        We don't have an account for <strong>{email}</strong>.
                      </p>
                      <p className="text-blue-700 dark:text-blue-300 text-sm mt-2">
                        We've sent you an email with information on how to get started with OpenBox.
                      </p>
                    </div>

                    <div className="bg-muted/50 rounded-lg p-4 border">
                      <p className="font-semibold text-foreground mb-2 flex items-center gap-2">
                        <Mail className="h-4 w-4" />
                        Looking for ShopJimmy.com?
                      </p>
                      <p className="text-sm text-muted-foreground">
                        <strong>OpenBox by ShopJimmy</strong> is a separate B2B wholesale platform for open-box appliances. 
                        Your ShopJimmy.com account won't work here — you'll need to apply for a business account.
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="bg-amber-50 dark:bg-amber-950/40 rounded-lg p-4 border-2 border-amber-300 dark:border-amber-700">
                      <p className="text-amber-800 dark:text-amber-200 font-medium text-base">
                        The password you entered doesn't match our records.
                      </p>
                    </div>

                    <div className="bg-muted/50 rounded-lg p-3 border">
                      <p className="font-medium text-foreground mb-1">What would you like to do?</p>
                      <ul className="text-sm space-y-1 list-disc list-inside text-muted-foreground">
                        <li>Double-check your password and try again</li>
                        <li>Reset your password using the "Forgot Password?" link</li>
                        <li>Browse our inventory without logging in</li>
                      </ul>
                    </div>
                  </>
                )}
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between pt-2">
            <Button
              variant="ghost"
              onClick={() => {
                setShowLoginHelpDialog(false);
                setLocation('/');
              }}
              data-testid="button-browse"
            >
              Browse Inventory
            </Button>
            <div className="flex gap-2">
              {loginErrorReason === 'ACCOUNT_INACTIVE' ? (
                <Button
                  onClick={() => {
                    setShowLoginHelpDialog(false);
                    setLocation('/');
                  }}
                  data-testid="button-continue-browsing"
                >
                  Continue Browsing
                </Button>
              ) : loginErrorReason === 'EMAIL_NOT_FOUND' ? (
                <Button
                  onClick={() => {
                    setShowLoginHelpDialog(false);
                    setLocation('/signup');
                  }}
                  data-testid="button-create-account"
                >
                  Apply for Account
                </Button>
              ) : (
                <>
                  <Button
                    variant="outline"
                    onClick={() => setShowLoginHelpDialog(false)}
                    data-testid="button-try-again"
                  >
                    Try Again
                  </Button>
                  <Button
                    onClick={() => {
                      setShowLoginHelpDialog(false);
                      setLocation('/signup');
                    }}
                    data-testid="button-create-account"
                  >
                    Apply for Account
                  </Button>
                </>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
